import { useState } from 'react';
import PropTypes from 'prop-types';
import MarkdownRenderer from './shared/MarkdownRenderer';
import UnknownKnowledgeForm from './UnknownKnowledgeForm';
import toast from 'react-hot-toast';

export default function ChatBubble({ message, isUser, timestamp, metadata, onShowDetails, onRedo, onFeedback, messageIndex, conversationId }) {
  const [feedback, setFeedback] = useState(metadata?.feedback || null); // 'up', 'down', or null
  const [copied, setCopied] = useState(false);

  // Only show action buttons for AI messages
  const hasMetadata = !isUser && metadata && (
    (metadata.function_calls && metadata.function_calls.length > 0) ||
    (metadata.sources && metadata.sources.length > 0)
  );

  // Check if unknown_knowledge_form was called and extract suggested question
  const unknownKnowledgeFormCall = !isUser && metadata?.function_calls?.find(
    fc => fc.name === 'unknown_knowledge_form'
  );
  const hasUnknownKnowledgeForm = !!unknownKnowledgeFormCall;
  const suggestedQuestion = unknownKnowledgeFormCall?.arguments?.suggested_question || '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleRedo = () => {
    if (onRedo) {
      onRedo(messageIndex);
    }
  };

  const handleFeedback = (type) => {
    const newFeedback = feedback === type ? null : type; // Toggle off if clicking same button
    setFeedback(newFeedback);

    if (onFeedback) {
      onFeedback(messageIndex, newFeedback);
    }

    if (newFeedback === 'up') {
      toast.success('Thanks for your feedback!');
    } else if (newFeedback === 'down') {
      toast('Feedback recorded', { icon: '👎' });
    }
  };

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} animate-fade-in-up`}
    >
      <div className="max-w-2xl">
        {/* Message Bubble */}
        <div
          className={`px-5 py-3 rounded-2xl transition-all duration-200 shadow-lg`}
          style={isUser ? {
            backgroundColor: 'var(--color-accent-primary)',
            color: 'var(--color-text-primary)',
            boxShadow: '0 10px 15px -3px var(--color-shadow)'
          } : {
            backgroundColor: 'var(--color-bg-secondary)',
            borderWidth: '1px',
            borderColor: 'var(--color-border-default)',
            color: 'var(--color-text-primary)'
          }}
        >
          {isUser ? (
            /* User message - plain text */
            <p className="text-sm sm:text-base whitespace-pre-wrap break-words">
              {message}
            </p>
          ) : (
            /* AI message - markdown rendered */
            <>
              <div className="text-sm sm:text-base">
                <MarkdownRenderer content={message} readOnly={true} />
              </div>

              {/* Unknown Knowledge Form - Inside AI Bubble */}
              {hasUnknownKnowledgeForm && (
                <UnknownKnowledgeForm
                  suggestedQuestion={suggestedQuestion}
                  conversationId={conversationId}
                  messageIndex={messageIndex}
                  isSubmitted={metadata?.form_submitted || false}
                />
              )}
            </>
          )}
        </div>

        {/* Action Buttons - Only for AI messages */}
        {!isUser && (
          <div className="flex items-center gap-1 mt-2 px-2">
            {/* Details Button */}
            {hasMetadata && (
              <button
                onClick={() => onShowDetails(message, metadata)}
                className="p-1.5 rounded-lg transition-colors group"
                style={{ color: 'var(--color-text-muted)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                  e.currentTarget.style.color = 'var(--color-text-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--color-text-muted)';
                }}
                title="View details"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}

            {/* Copy Button */}
            <button
              onClick={handleCopy}
              className="p-1.5 rounded-lg transition-colors group"
              style={{ color: copied ? 'var(--color-success)' : 'var(--color-text-muted)' }}
              onMouseEnter={(e) => {
                if (!copied) {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                  e.currentTarget.style.color = 'var(--color-text-primary)';
                }
              }}
              onMouseLeave={(e) => {
                if (!copied) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--color-text-muted)';
                }
              }}
              title="Copy message"
            >
              {copied ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>

            {/* Redo Button */}
            <button
              onClick={handleRedo}
              className="p-1.5 rounded-lg transition-colors group"
              style={{ color: 'var(--color-text-muted)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                e.currentTarget.style.color = 'var(--color-text-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-muted)';
              }}
              title="Regenerate response"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>

            {/* Divider */}
            <div className="w-px h-4 mx-1" style={{ backgroundColor: 'var(--color-border-default)' }}></div>

            {/* Thumbs Up Button */}
            <button
              onClick={() => handleFeedback('up')}
              className="p-1.5 rounded-lg transition-colors group"
              style={{
                backgroundColor: feedback === 'up' ? 'var(--color-bg-hover)' : 'transparent',
                color: feedback === 'up' ? 'var(--color-success)' : 'var(--color-text-muted)'
              }}
              onMouseEnter={(e) => {
                if (feedback !== 'up') {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                  e.currentTarget.style.color = 'var(--color-text-primary)';
                }
              }}
              onMouseLeave={(e) => {
                if (feedback !== 'up') {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--color-text-muted)';
                }
              }}
              title="Good response"
            >
              <svg
                className="w-4 h-4"
                fill={feedback === 'up' ? 'currentColor' : 'none'}
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
              </svg>
            </button>

            {/* Thumbs Down Button */}
            <button
              onClick={() => handleFeedback('down')}
              className="p-1.5 rounded-lg transition-colors group"
              style={{
                backgroundColor: feedback === 'down' ? 'var(--color-bg-hover)' : 'transparent',
                color: feedback === 'down' ? 'var(--color-error)' : 'var(--color-text-muted)'
              }}
              onMouseEnter={(e) => {
                if (feedback !== 'down') {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                  e.currentTarget.style.color = 'var(--color-text-primary)';
                }
              }}
              onMouseLeave={(e) => {
                if (feedback !== 'down') {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--color-text-muted)';
                }
              }}
              title="Bad response"
            >
              <svg
                className="w-4 h-4"
                fill={feedback === 'down' ? 'currentColor' : 'none'}
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

ChatBubble.propTypes = {
  message: PropTypes.string.isRequired,
  isUser: PropTypes.bool.isRequired,
  timestamp: PropTypes.string,
  metadata: PropTypes.object,
  onShowDetails: PropTypes.func,
  onRedo: PropTypes.func,
  onFeedback: PropTypes.func,
  messageIndex: PropTypes.number,
  conversationId: PropTypes.string,
};
